/**
 * Detects renderable artifacts in message content — large code blocks,
 * HTML documents, SVG, and file references suitable for the canvas panel.
 */

export interface Artifact {
  type: "html" | "code" | "svg" | "file";
  content: string;
  language?: string;
  title?: string;
  /** For file artifacts: the remote path on the agent VM (absolute or workspace-relative) */
  filePath?: string;
  /** For file artifacts: the file extension (lowercase, no dot) */
  fileExt?: string;
}

const MIN_CODE_LINES = 15;

/** File extensions we can preview or offer for download. */
const FILE_EXTENSIONS = new Set([
  "pdf", "csv", "tsv", "md", "txt", "json", "html", "htm", "xml", "yaml", "yml",
  "docx", "doc", "xlsx", "xls", "pptx", "ppt", "cab", "zip", "tar", "gz",
  "png", "jpg", "jpeg", "gif", "svg", "webp", "bmp", "ico",
]);

const MAX_FILE_ARTIFACTS = 12;

/**
 * Extract artifacts from markdown message content.
 * Returns an array of detected artifacts (may be empty).
 */
export function detectArtifacts(content: string): Artifact[] {
  const artifacts: Artifact[] = [];
  const seenPaths = new Set<string>();

  // ── Pass 1: fenced code blocks ────────────────────────────────────
  // Match ```lang\n...\n``` — html/svg become inline artifacts, large
  // code blocks become code artifacts. We deliberately also scan code
  // bodies in pass 2 so a file path mentioned inside a fenced block
  // still gets a chip.
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  let match: RegExpExecArray | null;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    const language = (match[1] || "").toLowerCase();
    const code = match[2];
    const lineCount = code.split("\n").length;

    if (
      language === "html" &&
      (code.includes("<html") || code.includes("<!DOCTYPE") || code.includes("<body"))
    ) {
      artifacts.push({
        type: "html",
        content: code,
        language: "html",
        title: extractHtmlTitle(code) || "HTML Document",
      });
      continue;
    }

    if (language === "svg" || (language === "xml" && code.includes("<svg"))) {
      artifacts.push({
        type: "svg",
        content: code,
        language: "svg",
        title: "SVG Image",
      });
      continue;
    }

    if (lineCount >= MIN_CODE_LINES) {
      artifacts.push({
        type: "code",
        content: code,
        language,
        title: language ? `${language} code` : "Code",
      });
    }
  }

  // ── Pass 2: file path references ──────────────────────────────────
  // Scan the entire message (incl. code-block bodies and markdown link
  // targets) for paths. Accepts:
  //   - absolute under any /home/<user>/ root (azureuser, openclaw, …)
  //   - tilde-prefixed paths (~/workspace/foo.csv)
  //   - workspace-relative paths (workspace/foo.csv)
  // and tolerates surrounding markdown noise: backticks, link parens,
  // angle brackets, sentence-trailing punctuation.
  const pathRegex =
    /(?:\/home\/[a-z_][a-z0-9_-]*\/|~\/|(?<![\w/.])workspace\/)[^\s`)\]\}>"',;]+/gi;

  let pathMatch: RegExpExecArray | null;
  while ((pathMatch = pathRegex.exec(content)) !== null) {
    let raw = pathMatch[0];
    // Strip trailing punctuation that often clings to paths in prose
    // (commas excluded already, but `.` at end of sentence is allowed
    // inside the regex because filenames can contain dots).
    raw = raw.replace(/[.!?:]+$/, "");

    const extMatch = raw.match(/\.([a-zA-Z0-9]{1,6})$/);
    if (!extMatch) continue;
    const ext = extMatch[1].toLowerCase();
    if (!FILE_EXTENSIONS.has(ext)) continue;

    // Normalise: tilde + workspace-relative both resolve server-side
    // via the agent's workspaceDir. Leave them as-is for the proxy
    // route to expand against the actual sshUser.
    const filePath = raw;
    if (seenPaths.has(filePath)) continue;
    seenPaths.add(filePath);

    if (artifacts.filter((a) => a.type === "file").length >= MAX_FILE_ARTIFACTS) break;

    const filename = filePath.split("/").pop() || filePath;
    artifacts.push({
      type: "file",
      content: "",
      filePath,
      fileExt: ext,
      title: filename,
    });
  }

  return artifacts;
}

function extractHtmlTitle(html: string): string | null {
  const titleMatch = html.match(/<title[^>]*>(.*?)<\/title>/i);
  return titleMatch?.[1]?.trim() || null;
}
